import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getDocFormList,
  getDocFormListSimple,
  getDocFormListHtml,
  getDocFormListAdmin,
  updateForm,
  updateFormsBatch,
  insertForm,
  deleteForm,
  getFormData,
  getDocFormCheckEmp,
} from '@entities/form/api/form';

/**
 * 양식 목록 조회 Query 훅 (전체)
 * - 양식 선택 페이지에서 양식 목록 표시에 사용
 * @param {number} companyCode - 회사 코드
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocFormListQuery(companyCode, options = {}) {
  return useQuery({
    queryKey: ['docFormList', companyCode],
    queryFn: () => getDocFormList(companyCode),
    enabled: !!companyCode,
    select: (data) => data?.resultData?.docTypeList || [],
    // 양식 목록은 자주 변경되지 않으므로 30분간 캐싱
    staleTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * 양식 목록 조회 Query 훅 (간단 버전)
 * - 문서 필터링에서 양식 선택 드롭다운에 사용
 * @param {number} companyCode - 회사 코드
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocFormListSimpleQuery(companyCode, options = {}) {
  return useQuery({
    queryKey: ['docFormListSimple', companyCode],
    queryFn: () => getDocFormListSimple(companyCode),
    enabled: !!companyCode,
    select: (data) => data?.resultData?.docTypeList || [],
    // 양식 목록은 자주 변경되지 않으므로 30분간 캐싱
    staleTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * 양식 목록 조회 Query 훅 (HTML 버전)
 * - 양식 선택 페이지에서 HTML 양식 목록 표시에 사용
 * @param {number} companyCode - 회사 코드
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocFormListHtmlQuery(companyCode, options = {}) {
  return useQuery({
    queryKey: ['docFormListHtml', companyCode],
    queryFn: () => getDocFormListHtml(companyCode),
    enabled: !!companyCode,
    select: (data) => data?.resultData?.docTypeList || [],
    // 양식 목록은 자주 변경되지 않으므로 30분간 캐싱
    staleTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * 양식 목록 조회 Query 훅 (어드민 전용)
 * - admin에서 사용하는 api
 * - 문서 양식 관리 페이지에서 사용
 * @param {number} companyCode - 회사 코드
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocFormListAdminQuery(companyCode, options = {}) {
  return useQuery({
    queryKey: ['docFormListAdmin', companyCode],
    queryFn: () => getDocFormListAdmin(companyCode),
    enabled: !!companyCode,
    select: (data) => data?.resultData?.docTypeList || [],
    // 양식 목록은 자주 변경되지 않으므로 30분간 캐싱
    staleTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * 양식 수정 Mutation 훅
 * - 양식 정보를 수정하는 API 호출
 * @param {Object} options - React Query mutation 옵션
 * @returns {UseMutationResult} mutation 결과
 */
function useUpdateFormMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(formData) {
      return updateForm(formData);
    },
    onSuccess: (data, variables, context) => {
      // 양식 목록 캐시만 무효화 (refetch는 목록 페이지 마운트 시 수행하여, 추가/편집 페이지에서 실패한 refetch로 error 상태가 남는 것 방지)
      queryClient.invalidateQueries({ queryKey: ['docFormListAdmin'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['docFormList'], refetchType: 'none' });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}

/**
 * 양식 일괄 수정 Mutation 훅 (배치)
 * - 여러 양식의 정보를 한 번의 요청으로 수정하는 API 호출
 * @param {Object} options - React Query mutation 옵션
 * @returns {UseMutationResult} mutation 결과
 */
function useUpdateFormsBatchMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(formDataList) {
      return updateFormsBatch(formDataList);
    },
    onSuccess: (data, variables, context) => {
      // 양식 목록 캐시만 무효화 (refetch는 목록 페이지 마운트 시 수행)
      queryClient.invalidateQueries({ queryKey: ['docFormListAdmin'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['docFormList'], refetchType: 'none' });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}

/**
 * 양식 추가 Mutation 훅
 * - 양식을 추가하는 API 호출
 * @param {Object} options - React Query mutation 옵션
 * @returns {UseMutationResult} mutation 결과
 */
function useInsertFormMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(formData) {
      return insertForm(formData);
    },
    onSuccess: (data, variables, context) => {
      // 양식 목록 캐시만 무효화 (refetch는 목록 페이지 마운트 시 수행)
      queryClient.invalidateQueries({ queryKey: ['docFormListAdmin'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['docFormList'], refetchType: 'none' });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}

/**
 * 양식 삭제 Mutation 훅
 * - 양식을 삭제하는 API 호출
 * @param {Object} options - React Query mutation 옵션
 * @returns {UseMutationResult} mutation 결과
 */
function useDeleteFormMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn({ docId, companyCode }) {
      return deleteForm(docId, companyCode);
    },
    onSuccess: (data, variables, context) => {
      // 양식 목록 캐시만 무효화 (refetch는 목록 페이지 마운트 시 수행)
      queryClient.invalidateQueries({ queryKey: ['docFormListAdmin'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['docFormList'], refetchType: 'none' });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options,
  });
}

/**
 * 양식 단일 조회 Query 훅 (편집용)
 * - 양식 편집 페이지에서 사용
 * @param {number} companyCode - 회사 코드
 * @param {number} docId - 문서 타입 ID
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useFormDataQuery(companyCode, docId, options = {}) {
  return useQuery({
    queryKey: ['formData', companyCode, docId],
    queryFn: () => getFormData(companyCode, docId),
    enabled: !!companyCode && !!docId,
    select: (data) => data?.resultData?.formData || null,
    ...options,
  });
}

/**
 * 양식 검토자 조회 Query 훅
 * - 양식 작성 화면에서 검토자 정보를 가져올 때 사용
 * @param {number} docId - 문서 타입 ID
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocFormCheckEmpQuery(docId, options = {}) {
  return useQuery({
    queryKey: ['docFormCheckEmp', docId],
    queryFn: () => getDocFormCheckEmp(docId),
    enabled: !!docId,
    select: (data) => data?.resultData || null,
    ...options,
  });
}

export {
  useDocFormListQuery,
  useDocFormListSimpleQuery,
  useDocFormListHtmlQuery,
  useDocFormListAdminQuery,
  useUpdateFormMutation,
  useUpdateFormsBatchMutation,
  useInsertFormMutation,
  useDeleteFormMutation,
  useFormDataQuery,
  useDocFormCheckEmpQuery,
};
